import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, Loader2, RefreshCw, History, GitCompare } from 'lucide-react';
import SupplierComparison from '../components/SupplierComparison';
import MessageDialog from '../components/MessageDialog';
import SupplierCard from '../components/SupplierCard';
import SummaryHeader from '../components/SummaryHeader';
import { toast } from 'sonner';

const HTTP_NOT_FOUND = 404;

const Results = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [savedMap, setSavedMap] = useState({});
  const [savedStatusMap, setSavedStatusMap] = useState({});
  const [savingName, setSavingName] = useState(null);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const navigate = useNavigate();

  const toggleCompareSelect = (name) => {
    setSelectedForCompare((prev) =>
      prev.includes(name)
        ? prev.filter((n) => n !== name)
        : prev.length >= 3 ? prev : [...prev, name]
    );
  };
  const clearCompareSelection = () => setSelectedForCompare([]);

  const fetchRequest = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/api/requests/${id}`);
      setRequest(data);
    } catch (error) {
      if (error.response?.status === HTTP_NOT_FOUND) setRequest(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchRequest(); }, [fetchRequest]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setShowPrevious(false);
    setSelectedForCompare([]);
    try {
      const { data } = await api.post(`/api/requests/${id}/refresh`);
      setRequest(data);
    } finally {
      setRefreshing(false);
    }
  };

  const openMessageDialog = (supplier) => {
    setSelectedSupplier(supplier);
    setDialogOpen(true);
  };

  // Saved suppliers ----------------------------------------------------------
  // We use GET /saved-suppliers so we capture both id and status (Phase C).
  // The /check endpoint only returns ids — kept available but unused here.
  const reloadSavedMap = React.useCallback(async () => {
    if (!request) return;
    try {
      const names = new Set((request.suppliers || []).map((s) => s.name));
      if (names.size === 0) { setSavedMap({}); setSavedStatusMap({}); return; }
      const { data } = await api.get('/api/saved-suppliers');
      const idMap = {};
      const statusMap = {};
      for (const item of data || []) {
        const supName = item?.supplier?.name;
        if (!supName || !names.has(supName)) continue;
        // Scope to current request when available; fall back to any save for this name
        if (!item.request_id || item.request_id === id) {
          idMap[supName] = item.id;
          statusMap[supName] = item.status || 'not_contacted';
        }
      }
      setSavedMap(idMap);
      setSavedStatusMap(statusMap);
    } catch (e) {
      setSavedMap({});
      setSavedStatusMap({});
    }
  }, [request, id]);

  useEffect(() => { reloadSavedMap(); }, [reloadSavedMap]);

  const toggleSaveSupplier = async (supplier) => {
    if (!request) return;
    setSavingName(supplier.name);
    try {
      const existingId = savedMap[supplier.name];
      if (existingId) {
        await api.delete(`/api/saved-suppliers/${existingId}`);
        setSavedMap((m) => {
          const next = { ...m };
          delete next[supplier.name];
          return next;
        });
        toast.success(t('supplierUnsaved'));
      } else {
        const { data } = await api.post('/api/saved-suppliers', {
          request_id: id,
          product_name: request.product_name,
          quantity: request.quantity,
          budget: request.budget,
          supplier,
          notes: '',
          tags: [],
        });
        setSavedMap((m) => ({ ...m, [supplier.name]: data.id }));
        toast.success(t('supplierSaved'));
      }
    } catch (e) {
      toast.error(t('templateError'));
    } finally {
      setSavingName(null);
    }
  };

  const formatTimestamp = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex flex-col items-center justify-center py-20">
          <div className="spinner mb-4"></div>
          <p className="text-slate-600">{t('analyzingSuppliersLoading')}</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-slate-600">{t('requestNotFound')}</p>
        </div>
      </div>
    );
  }

  const displayedSuppliers = showPrevious && request.previous_suppliers
    ? request.previous_suppliers
    : request.suppliers;
  const hasPrevious = request.previous_suppliers && request.previous_suppliers.length > 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-to-dashboard-button">
          <ArrowLeft className="w-4 h-4 mr-2" />{t('backToDashboard')}
        </Button>

        {/* Summary Header — Decision Support Insights */}
        <SummaryHeader suppliers={request.suppliers} t={t} />


        {/* Request Details */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{request.product_name}</h1>
            <div className="flex items-center gap-2 flex-wrap">
              {hasPrevious && (
                <Button variant="outline" size="sm" onClick={() => setShowPrevious(!showPrevious)} data-testid="toggle-previous-button">
                  <History className="w-4 h-4 mr-2" />{showPrevious ? t('viewCurrentResults') : t('viewPreviousResults')}
                </Button>
              )}
              <Button size="sm" onClick={handleRefresh} disabled={refreshing} className="bg-blue-600 hover:bg-blue-700" data-testid="refresh-search-button">
                {refreshing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('refreshing')}</> : <><RefreshCw className="w-4 h-4 mr-2" />{t('refreshSearch')}</>}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div><span className="text-slate-500">{t('quantity')}</span><p className="font-medium text-slate-900">{request.quantity} {t('units')}</p></div>
            <div><span className="text-slate-500">{t('budget')}</span><p className="font-medium text-slate-900">${request.budget.toLocaleString()}</p></div>
            <div><span className="text-slate-500">{t('suppliersFound')}</span><p className="font-medium text-slate-900">{displayedSuppliers.length}</p></div>
          </div>
          {request.updated_at && (
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
              <Clock className="w-3 h-3" />{t('lastUpdated')}: {formatTimestamp(request.updated_at)}
            </div>
          )}
          {request.notes && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <span className="text-sm text-slate-500">{t('notes')}:</span>
              <p className="text-sm text-slate-700 mt-1">{request.notes}</p>
            </div>
          )}
        </div>

        {showPrevious && hasPrevious && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-2 text-sm text-amber-700" data-testid="previous-results-banner">
            <History className="w-4 h-4" />{t('previousResults')}
          </div>
        )}

        {/* Compare bar — appears when 2+ suppliers selected */}
        {selectedForCompare.length >= 1 && (
          <div className="sticky top-2 z-30 mb-4 bg-blue-600 text-white rounded-lg shadow-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3" data-testid="compare-bar">
            <div className="flex items-center gap-3 text-sm">
              <GitCompare className="w-4 h-4 shrink-0" />
              <span data-testid="compare-bar-count">
                <span className="font-bold">{selectedForCompare.length}</span> / 3 {t('selectedForCompare')}
              </span>
              {selectedForCompare.length >= 3 && (
                <span className="hidden sm:inline text-xs text-blue-100">— {t('maxComparisonReached')}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={clearCompareSelection}
                className="text-white hover:bg-blue-700 hover:text-white"
                data-testid="compare-clear-button"
              >
                {t('clearComparison')}
              </Button>
              <Button
                size="sm"
                onClick={() => setCompareOpen(true)}
                disabled={selectedForCompare.length < 2}
                className="bg-white text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="open-comparison-button"
              >
                <GitCompare className="w-4 h-4 mr-2" />
                {t('compareSuppliersBtn')}
              </Button>
            </div>
          </div>
        )}

        {/* Suppliers List */}
        <div className="space-y-4 stagger-fade-in" data-testid="suppliers-list">
          {displayedSuppliers.map((supplier, index) => (
            <SupplierCard
              key={`${supplier.name}-${index}`}
              supplier={supplier}
              index={index}
              t={t}
              onGenerateMessage={openMessageDialog}
              isSelected={selectedForCompare.includes(supplier.name)}
              canSelect={selectedForCompare.length < 3}
              onToggleSelect={toggleCompareSelect}
              isSaved={Boolean(savedMap[supplier.name])}
              isSaving={savingName === supplier.name}
              onToggleSave={toggleSaveSupplier}
            />
          ))}
        </div>
      </main>

      {/* Comparison Modal */}
      <SupplierComparison
        open={compareOpen}
        onOpenChange={setCompareOpen}
        suppliers={displayedSuppliers.filter((s) => selectedForCompare.includes(s.name))}
        t={t}
        requestId={request?.id}
        language={language}
        onGenerateMessage={(supplier) => {
          setCompareOpen(false);
          openMessageDialog(supplier);
        }}
      />

      {/* Message Dialog (extracted to a reusable component) */}
      <MessageDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        supplier={selectedSupplier}
        context={request ? { product_name: request.product_name, quantity: request.quantity, budget: request.budget } : null}
        defaultLanguage={language === 'fr' ? 'fr' : 'en'}
        savedId={selectedSupplier ? savedMap[selectedSupplier.name] : undefined}
        savedStatus={selectedSupplier ? savedStatusMap[selectedSupplier.name] : undefined}
        onStatusChanged={() => reloadSavedMap()}
      />
    </div>
  );
};

export default Results;
